import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { translateStatus, translateRisk } from "../../lib/constants";
import { useAuth } from "../../App";

// Entity List Page Component
export const EntityListPage = ({ type, title }) => {
  const [entities, setEntities] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const fetchEntities = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/${type}${params}`);
      setEntities(response.data);
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      toast.error(`Erro ao carregar ${title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  useEffect(() => {
    fetchEntities();
    fetchTenants();
  }, [type, search]);

  const getTenantName = (tenantId) => {
    if (!tenantId) return '-';
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    return tenant?.name || tenantId;
  };

  const idField = type === 'clients' ? 'client_id' : type === 'suppliers' ? 'supplier_id' : 'employee_id';
  const detailPath = type === 'clients' ? '/clients' : type === 'suppliers' ? '/suppliers' : '/employees';

  const handleExportExcel = async () => {
    try {
      const response = await api.get(`/${type}/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/${type}/export/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      toast.error('Erro ao exportar PDF');
    }
  };

  return (
    <div data-testid={`${type}-page`} className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">{title}</h1>
          <p className="text-muted-foreground">Gerir {title.toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          {type === 'employees' && (
            <>
              <button
                onClick={handleExportExcel}
                data-testid="export-employees-excel-btn"
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-2 text-sm"
                title="Exportar para Excel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel
              </button>
              <button
                onClick={handleExportPDF}
                data-testid="export-employees-pdf-btn"
                className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
                title="Exportar para PDF"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                PDF
              </button>
            </>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
            data-testid={`create-${type}-btn`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Pesquisar..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : entities.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <p className="text-muted-foreground">Nenhum registo encontrado</p>
          <button onClick={() => setShowCreateModal(true)} className="mt-4 text-primary hover:underline">
            Criar o primeiro registo
          </button>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                {isSuperAdmin && <th>Tenant</th>}
                <th>NIF/Email</th>
                <th>Status</th>
                <th>Risco</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entities.map((entity) => (
                <tr key={entity[idField]} className="cursor-pointer" onClick={() => navigate(`${detailPath}/${entity[idField]}`)}>
                  <td className="font-medium">{entity.name}</td>
                  {isSuperAdmin && (
                    <td>
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                        {getTenantName(entity.tenant_id)}
                      </span>
                    </td>
                  )}
                  <td className="text-muted-foreground">{entity.tax_id || entity.email || '-'}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      entity.status === 'approved' || entity.status === 'active' ? 'status-badge-approved' :
                      entity.status === 'rejected' || entity.status === 'terminated' ? 'status-badge-rejected' : 'status-badge-pending'
                    }`}>
                      {translateStatus(entity.status)}
                    </span>
                  </td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      entity.risk_level === 'high' ? 'risk-badge-high' :
                      entity.risk_level === 'medium' ? 'risk-badge-medium' : 'risk-badge-low'
                    }`}>
                      {translateRisk(entity.risk_level)}
                    </span>
                  </td>
                  <td>
                    <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreateEntityModal
          type={type}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); fetchEntities(); }}
        />
      )}
    </div>
  );
};

// Create Entity Modal
export const CreateEntityModal = ({ type, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '', email: '', tax_id: '', address: '', country: '', industry: '',
    contact_email: '', contact_phone: '', department: '', position: '',
    crc_code: '', rcbe_code: '', credit_limit: '', cae_code: '', legal_form: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = type === 'clients' ? '/clients' : type === 'suppliers' ? '/suppliers' : '/employees';
      let payload;
      
      if (type === 'employees') {
        payload = { name: formData.name, email: formData.email, department: formData.department, position: formData.position, tax_id: formData.tax_id };
      } else {
        payload = { 
          name: formData.name, tax_id: formData.tax_id, address: formData.address, country: formData.country, 
          industry: formData.industry, contact_email: formData.contact_email, contact_phone: formData.contact_phone,
          crc_code: formData.crc_code || null, rcbe_code: formData.rcbe_code || null,
          credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
          cae_code: formData.cae_code || null, legal_form: formData.legal_form || null,
        };
      }
      
      await api.post(endpoint, payload);
      toast.success('Registo criado com sucesso');
      onCreated();
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Erro ao criar registo';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-xl font-semibold font-['Plus_Jakarta_Sans']">
            {type === 'clients' ? 'Novo Cliente' : type === 'suppliers' ? 'Novo Fornecedor' : 'Novo Colaborador'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg" data-testid="close-modal-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Nome *</label>
            <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="name-input" />
          </div>

          {type === 'employees' ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">NIF</label>
                  <input type="text" value={formData.tax_id} onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Departamento</label>
                  <input type="text" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Cargo</label>
                  <input type="text" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium mb-1">NIF/NIPC *</label>
                <input type="text" required value={formData.tax_id} onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="tax-id-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Morada</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">País</label>
                  <input type="text" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Indústria</label>
                  <input type="text" value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email de Contacto</label>
                  <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefone</label>
                  <input type="text" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="submit-btn">
              {loading ? 'A guardar...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Wrapper Pages
export const ClientsPage = () => <EntityListPage type="clients" title="Clientes (KYC)" />;
export const SuppliersPage = () => <EntityListPage type="suppliers" title="Fornecedores (KYS)" />;
export const EmployeesPage = () => <EntityListPage type="employees" title="Colaboradores (KYE)" />;
